(function () {
  "use strict";

  /* ==========================================================================
     Skins the Shopify Forms embed (the form that creates the company +
     customer) so it looks and behaves like the native "Cerere de activare
     cont" card in sections/b2b-register.liquid: CUI-first reveal, ANAF
     autofill + lock, kreg field styling.

     The embed is a React app inside an open shadow root, so theme CSS can't
     reach it. Instead:
     - look: a <style> is injected into the shadow root (the .kl custom
       properties inherit across the shadow boundary, so tokens stay in sync);
     - layout: field wrappers get data attributes + a 2-column grid with
       explicit `order`, mirroring the kreg field rows;
     - feel: everything except the CUI field stays hidden until an ANAF
       lookup ran (or the user opts into manual entry) — same flow as
       assets/b2b-register.js;
     - values are set through the React value setter so the app registers
       the autofill (same technique as assets/b2b-anaf-wizard.js, which this
       file borrows its shadow-DOM structure knowledge from).
     ========================================================================== */

  // ---- CONFIG -------------------------------------------------------------
  var PROXY_URL = "https://middleware.klintensiv.ro/api/anaf";
  var MIN_CUI_LEN = 6;
  var INACTIVE_RE = /radia|întrerup|intrerup|suspend|dizolv|lichid|insolven|faliment|inactiv/i;

  // Field handles as they appear in the Shopify Forms form:
  var CUI_ID = "custom#cui_companie";
  var NAME_ID = "company_name";
  var REG_ID = "custom#nr_registrul_comerului";
  var TVA_NAME = "custom#pltitor_tva"; // radio group name; values "Da"/"Nu"
  var ANAF_DETAIL_FIELDS = [NAME_ID, REG_ID, TVA_NAME];

  var PASSWORD_RE = /parol|password/i;

  // Visual order + row pairing (pair = half-width, two per row): only
  // domeniu + volum share a row; everything else is a full-width row.
  // Matched against id + name + aria-controls of the wrapper's controls.
  var LAYOUT_RULES = [
    { re: /cui/i, order: 10, pair: false },
    { re: /company|denumire|firma/i, order: 20, pair: false },
    { re: /comerului|reg_com/i, order: 30, pair: true },
    { re: /tva/i, order: 40, pair: true },
    { re: /domeniu/i, order: 50, pair: true },
    { re: /volum/i, order: 60, pair: true },
    { re: /categorii|interes/i, order: 65, pair: false },
    { re: /first_name/i, order: 70, pair: true },
    { re: /last_name/i, order: 80, pair: true },
    { re: /phone/i, order: 90, pair: false },
    { re: /email/i, order: 100, pair: false },
    { re: /mesaj|message|\bbody\b/i, order: 110, pair: false },
    { re: /termen|condi|acord|gdpr|confidential|autoriz/i, order: 130, pair: false },
  ];
  var ORDER_NOTE = "120";
  var ORDER_SUBMIT = "140";
  var ORDER_FOOT = "150";
  var ORDER_DEFAULT = "115";

  // ---- state ----------------------------------------------------------------
  var revealed = false;   // CUI-first: rest of the form hidden until true
  var anafLocked = false; // ANAF-sourced fields locked after a valid lookup
  var applying = false;

  function getWrap() {
    return document.querySelector("[data-kreg-embed]");
  }

  function getRoot() {
    var wrap = getWrap();
    var host = wrap && wrap.querySelector("shopify-forms-embed");
    return host && host.shadowRoot ? host.shadowRoot : null;
  }

  function cfg(name, fallback) {
    var wrap = getWrap();
    var v = wrap && wrap.getAttribute("data-" + name);
    return v || fallback;
  }

  // ---- React-safe helpers (from b2b-anaf-wizard.js) -------------------------

  function setReactValue(el, value) {
    if (!el) return;
    var proto =
      el.tagName === "TEXTAREA"
        ? window.HTMLTextAreaElement.prototype
        : window.HTMLInputElement.prototype;
    var setter = Object.getOwnPropertyDescriptor(proto, "value").set;
    setter.call(el, value);
    el.dispatchEvent(new Event("input", { bubbles: true }));
    el.dispatchEvent(new Event("change", { bubbles: true }));
  }

  function setRadio(root, name, value) {
    var radio = root.querySelector(
      'input[type="radio"][name="' + name + '"][value="' + value + '"]'
    );
    if (radio && !radio.checked) radio.click(); // click drives React state
  }

  function findTextNode(el) {
    for (var i = 0; i < el.childNodes.length; i++) {
      var n = el.childNodes[i];
      if (n.nodeType === 3 && n.nodeValue.trim()) return n;
      if (n.nodeType === 1) {
        var deep = findTextNode(n);
        if (deep) return deep;
      }
    }
    return null;
  }

  // ---- injected stylesheet ---------------------------------------------------
  // Replicates assets/b2b-register.css field styling inside the shadow root.
  // Design-token custom properties inherit from the surrounding .kl scope;
  // literals are kept as fallbacks.

  var CHEVRON =
    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%2351647A' stroke-width='2'><path d='m6 9 6 6 6-6'/></svg>\")";
  var CHECKMARK =
    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'><path d='M20 6 9 17l-5-5'/></svg>\")";
  var SKIN_CSS = [
    "form,form *{font-family:var(--kl-font,'Poppins',system-ui,sans-serif);box-sizing:border-box;}",
    "form{color:var(--navy-900,#0B2440);}",
    "form a{color:var(--blue-600,#1763AE);font-weight:600;text-decoration:none;}",
    "form a:hover{color:var(--navy-700,#14406E);text-decoration:underline;}",

    /* Strip the app's own field chrome (bordered containers, floating-label
       spacing, fixed widths) — the kreg skin repaints everything below. */
    "form{width:100%;max-width:100%;}",
    "[data-kregf] div{border:0;box-shadow:none;background:transparent;padding:0;margin:0;max-width:100%;}",
    "[data-kregf] fieldset{border:0;padding:0;margin:0;min-width:0;}",
    /* the app's floating labels are hoisted into ::before wrapper labels;
       the originals stay screen-reader-visible only */
    ".kregf-sr{position:absolute !important;width:1px !important;height:1px !important;padding:0 !important;margin:-1px !important;overflow:hidden !important;clip:rect(0,0,0,0) !important;white-space:nowrap !important;border:0 !important;}",

    /* field wrappers (tagged by apply()) */
    "[data-kregf]{margin:0 0 0px;min-width:0;max-width:100%;text-align:left;}",
    "[data-kregf-label]::before{content:attr(data-kregf-label);display:block;font-size:14px;font-weight:600;margin:0 0 7px;color:var(--navy-900,#0B2440);line-height:1.4;}",
    /* the app may render several label nodes per field (floating-label
       animation states) — visually hide them all in hoisted wrappers;
       option labels in tagged groups (tva/check/checks) stay visible */
    "[data-kregf-label]:not([data-kregf-kind]) label,[data-kregf-kind='dd'] label,[data-kregf-kind='dd'] p:not([role]){position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;}",

    /* text-ish inputs */
    "[data-kregf] input[type='text'],[data-kregf] input[type='email'],[data-kregf] input[type='tel'],[data-kregf] input[type='number'],[data-kregf] input:not([type]),[data-kregf] textarea{width:93%;height:48px;border:1.6px solid #C6D6E6;border-radius:12px;padding:0 14px;font-size:15px;color:var(--navy-900,#0B2440);background:#fff;transition:border-color .15s,box-shadow .15s;appearance:none;}",
    "[data-kregf] input[id='custom#nr_registrul_comerului']{width:85%;}",
    "[data-kregf] input[id='first_name'],[data-kregf] input[id='last_name']{width:87%;}",
    "[data-kregf] textarea{height:auto;padding:12px 14px;resize:vertical;min-height:84px;line-height:1.5;}",
    "[data-kregf] input:focus,[data-kregf] textarea:focus{outline:none;border-color:var(--blue-500,#2F86D9);box-shadow:0 0 0 3px rgba(59,130,196,.25);}",
    "[data-kregf] input[readonly]{background:#f3f4f6;color:var(--slate,#51647A);cursor:default;}",
    /* !important: the app hides native placeholders (color:transparent in
       its constructed stylesheet) under its floating labels — ours must show */
    "[data-kregf] input::placeholder,[data-kregf] textarea::placeholder{color:#9AACC0 !important;opacity:1 !important;}",

    /* dropdown widgets (button + popover list); the phone field's small
       country selector keeps the app's own compact styling */
    "[data-kregf] button[aria-controls$='-selector-dropdown']:not([aria-controls='country-selector-dropdown']){display:flex;width:99%;height:48px;align-items:center;justify-content:flex-start;text-align:left;border:1.6px solid #C6D6E6;border-radius:12px;padding:0 40px 0 14px;font-size:15px;color:var(--navy-900,#0B2440);background:#fff " + CHEVRON + " no-repeat right 13px center;cursor:pointer;transition:border-color .15s,box-shadow .15s;}",
    "[data-kregf] button[aria-controls$='-selector-dropdown']:not([aria-controls='country-selector-dropdown']) svg{display:none;}",
    "[data-kregf] button[aria-controls$='-selector-dropdown']:focus{outline:none;border-color:var(--blue-500,#2F86D9);box-shadow:0 0 0 3px rgba(59,130,196,.25);}",
    "[data-kregf] [id$='-selector-dropdown']:not([id='country-selector-dropdown']){background:#fff;border:1px solid var(--line,#E7EDF4);border-radius:12px;box-shadow:var(--shadow-modal,0 20px 64px rgba(11,36,64,.22));overflow:auto;max-height:300px;}",
    "[data-kregf] [id$='-selector-dropdown'] li,[data-kregf] [id$='-selector-dropdown'] [role='option']{font-size:14px;color:var(--navy-900,#0B2440);cursor:pointer;padding:10px 14px;}",
    "[data-kregf] [id$='-selector-dropdown'] li:hover,[data-kregf] [id$='-selector-dropdown'] [role='option']:hover{background:var(--sky-1,#E7F0F9);}",
    /* phone: only Romanian companies register — drop the country selector
       (the app defaults to RO / +40) and let the tel input fill the row
       like every other field (the container is a flex row in the app CSS) */
    ".phone-country-selector,#country-selector-dropdown{display:none !important;}",
    "[data-kregf][class*='PhoneInputContainer']{display:block;}",

    /* Plătitor TVA — Da/Nu radios in one calm row (the app's radio markup
       varies, so no chip boxes — just the kreg radio dots) */
    "[data-kregf-kind='tva']{padding-left:12px;}",
    "[data-kregf-kind='tva'] fieldset{display:flex;align-items:center;flex-wrap:wrap;gap:12px 26px;min-height:48px;}",
    "[data-kregf-kind='tva'] fieldset label{display:inline-flex;align-items:center;gap:8px;font-size:14.5px;font-weight:600;margin:0;cursor:pointer;color:var(--navy-900,#0B2440);}",
    "[data-kregf] input[type='radio']{appearance:none;width:18px;height:18px;margin:0;border:1.6px solid #c6d6e6;border-radius:50%;flex:none;background:#fff;cursor:pointer;transition:all .15s;}",
    "[data-kregf] input[type='radio']:checked{border-color:var(--blue-600,#1763AE);box-shadow:inset 0 0 0 4px var(--blue-600,#1763AE);}",

    /* checkbox groups (categorii de interes) — bordered chips, 2 per row */
    /* grid lives on the wrapper: the app's option markup varies, so any
       intermediate wrapper divs are flattened with display:contents and the
       hoisted ::before group label spans the full row */
    "[data-kregf-kind='checks']{display:grid;grid-template-columns:1fr 1fr;gap:10px;}",
    "[data-kregf-kind='checks']::before{grid-column:1 / -1;margin-bottom:0;}",
    "[data-kregf-kind='checks'] div,[data-kregf-kind='checks'] fieldset,[data-kregf-kind='checks'] [role='group'],[data-kregf-kind='checks'] ul,[data-kregf-kind='checks'] li{display:contents;}",
    "@media (max-width:520px){[data-kregf-kind='checks']{grid-template-columns:1fr;}}",
    "[data-kregf-kind='checks'] label{display:flex;gap:10px;align-items:center;border:1.5px solid var(--line,#E7EDF4);border-radius:10px;padding:12px 13px;cursor:pointer;font-size:13.5px;font-weight:500;color:var(--navy-900,#0B2440);margin:0;min-width:0;transition:border-color .15s,background .15s;}",
    "[data-kregf-kind='checks'] label:hover{border-color:var(--blue-500,#2F86D9);}",
    "[data-kregf-kind='checks'] label:has(input:checked){border-color:var(--blue-600,#1763AE);background:var(--sky-1,#E7F0F9);color:var(--blue-600,#1763AE);font-weight:600;}",

    /* single consent / terms checkbox */
    "[data-kregf-kind='check'] label{display:inline-flex;align-items:flex-start;gap:9px;cursor:pointer;font-size:13.5px;font-weight:500;color:var(--navy-900,#0B2440);line-height:1.5;margin:0;}",
    "[data-kregf] input[type='checkbox']{appearance:none;width:20px;height:20px;margin:1px 0 0;border:1.6px solid #c6d6e6;border-radius:6px;flex:none;background:#fff center/13px no-repeat;cursor:pointer;transition:all .15s;}",
    "[data-kregf] input[type='checkbox']:checked{background-color:var(--blue-600,#1763AE);border-color:var(--blue-600,#1763AE);background-image:" + CHECKMARK + ";}",

    /* error text from the embed's own validation */
    "[data-kregf] [role='alert'],[data-kregf] [id*='error' i]{color:var(--red,#C0492F);font-size:12px;line-height:1.4;margin-top:5px;}",

    /* submit — .btn.btn--primary.btn--lg.btn--block */
    /* same type as #kregf-search — the app's button class sets its own
       font-family, which outranks the generic `form *` rule */
    /* `form` prefix: the app styles this button from a constructed
       stylesheet whose selector ties ours on specificity and wins on order —
       the extra element selector breaks the tie in our favor */
    "form button[type='submit']{display:inline-flex;width:89%;align-items:center;justify-content:center;gap:9px;background:var(--blue-600,#1763AE);color:#fff;border:0;border-radius:14px;padding:16px 28px;font-family:var(--kl-font,'Poppins',system-ui,sans-serif);font-size:15px;font-weight:600;line-height:1.4;cursor:pointer;box-shadow:0 1px 2px rgba(23,99,174,.20),0 8px 22px rgba(23,99,174,.22);transition:transform .14s,background .18s,box-shadow .2s;outline:none;}",
    "form button[type='submit']:hover{background:var(--navy-700,#14406E);transform:translateY(-1px);box-shadow:0 2px 5px rgba(23,99,174,.24),0 16px 34px rgba(23,99,174,.30);}",
    "form button[type='submit']:active{transform:translateY(1px);}",
    "form button[type='submit']:focus-visible{outline:none;box-shadow:0 0 0 3px rgba(59,130,196,.40);}",

    /* injected CUI controls */
    "#kregf-search{position:absolute;top:27px;right:0;width:132px;height:48px;border:0;border-radius:12px;padding:0;white-space:nowrap;background:var(--blue-600,#1763AE);color:#fff;font-size:15px;font-weight:600;cursor:pointer;box-shadow:0 1px 2px rgba(23,99,174,.20),0 8px 22px rgba(23,99,174,.22);transition:transform .14s,background .18s;}",
    "#kregf-search:hover{background:var(--navy-700,#14406E);transform:translateY(-1px);}",
    "#kregf-search[disabled]{opacity:.6;cursor:default;pointer-events:none;}",
    "#kregf-status{font-size:12px;line-height:1.4;margin:7px 2px 0;color:var(--slate,#51647A);}",
    "#kregf-status.is-error{color:var(--red,#C0492F);}",
    "#kregf-status.is-ok{color:#13794a;}",
    "#kregf-manual{display:inline-block;margin-top:10px;font-size:14px;font-weight:600;color:var(--blue-600,#1763AE);background:none;border:0;padding:0;cursor:pointer;}",
    "#kregf-manual:hover{color:var(--navy-700,#14406E);text-decoration:underline;}",

    /* injected note + foot (cloned from light-DOM templates) */
    ".kregf-note{display:flex;gap:11px;align-items:flex-start;font-size:12.5px;color:var(--slate,#51647A);background:var(--sky-0,#F5F9FD);border:1px solid var(--line,#E7EDF4);border-radius:var(--r-md,10px);padding:13px 15px;margin:2px 0 16px;text-align:left;line-height:1.5;}",
    ".kregf-note p{margin:0;display:inline;}",
    ".kregf-note svg{width:17px;height:17px;color:var(--blue-600,#1763AE);flex:none;margin-top:1px;}",
    ".kregf-foot{text-align:center;font-size:14px;color:var(--slate,#51647A);margin:0;}",

    /* CUI row: if the flexed container also holds the label, the label
       takes the full first line and input + button share the second */
    /* CUI row: plain block flow (label / input / status / manual link) with
       the "Verifică CUI" button absolutely pinned top-right, beside the
       input. No flex/grid — the app's re-renders can't disturb it. The 27px
       offset clears the hoisted ::before label (14px × 1.4 + 7px margin). */
    "#kregf-cui-row{display:block !important;position:relative;padding:0;}",
    "#kregf-cui-row input{display:block;width:calc(100% - 172px);min-width:0;height:48px;}",
    "#kregf-cui-row #kregf-status{display:block;}",
    "#kregf-cui-row #kregf-manual{display:inline-block;}",

    /* narrow screens: collapse the domeniu/volum pair to one per row
       (!important beats the inline grid-template set by apply()) */
    "@media (max-width:520px){#kregf-grid{grid-template-columns:minmax(0,1fr) !important;}#kregf-cui-row input{width:100%;}#kregf-search{position:static;width:100%;margin-top:10px;}}",
  ].join("\n");

  function ensureStyle(root) {
    if (!root.getElementById("kregf-skin")) {
      var style = document.createElement("style");
      style.id = "kregf-skin";
      style.textContent = SKIN_CSS;
      root.appendChild(style);
    }
  }

  // ---- shadow-DOM structure scan (from b2b-anaf-wizard.js) ------------------

  function fieldKey(c) {
    var ac = (c.getAttribute && c.getAttribute("aria-controls")) || "";
    return (c.id || "") + " " + (c.name || "") + " " + ac;
  }

  // Walk down through single-child layout divs until we reach the element
  // whose children are the individual field rows.
  function getFieldsContainer(form) {
    var el = form;
    for (;;) {
      var kids = Array.prototype.filter.call(el.children, function (k) {
        return (
          k.matches &&
          (k.matches("input,select,textarea,button") ||
            k.querySelector("input,select,textarea,button"))
        );
      });
      if (kids.length === 1 && kids[0] !== el) el = kids[0];
      else return el;
    }
  }

  function isOurs(el) {
    return el.id && el.id.indexOf("kregf-") === 0;
  }

  function scan(root) {
    var form = root.querySelector("form");
    if (!form) return null;
    var container = getFieldsContainer(form);
    var wrappers = [];
    var seenField = false;
    Array.prototype.forEach.call(container.children, function (el) {
      if (isOurs(el) || el.tagName === "STYLE") return;
      var w = {
        el: el,
        kind: null,      // 'cui' | 'detail' | 'rest' | 'pw' | 'submit' | 'text'
        order: ORDER_DEFAULT,
        pair: false,
        tag: "",         // extra data-kregf-kind value: 'tva' | 'dd' | 'check' | 'checks'
        key: "",         // accumulated id/name/aria-controls of the controls
      };
      var controls =
        el.matches && el.matches("input,select,textarea,button")
          ? [el]
          : el.querySelectorAll("input,select,textarea,button");
      var isField = false;
      Array.prototype.forEach.call(controls, function (c) {
        var key = fieldKey(c);
        if (c.type === "submit") {
          w.kind = "submit";
          return;
        }
        if (c.type === "hidden") return;
        isField = true;
        w.key += " " + key;
        if (c.type === "password" || PASSWORD_RE.test(key)) w.kind = "pw";
        if (c.id === CUI_ID || c.name === CUI_ID) w.kind = w.kind || "cui";
        if (
          ANAF_DETAIL_FIELDS.indexOf(c.id) !== -1 ||
          ANAF_DETAIL_FIELDS.indexOf(c.name) !== -1
        ) w.kind = w.kind || "detail";
        if (c.type === "radio" && c.name === TVA_NAME) w.tag = "tva";
        if (c.type === "checkbox" && w.tag !== "tva") {
          // one checkbox = consent row; several = chip group (categorii)
          w.tag = w.tag === "check" || w.tag === "checks" ? "checks" : "check";
        }
        var ac = (c.getAttribute && c.getAttribute("aria-controls")) || "";
        if (/-selector-dropdown$/.test(ac) && ac !== "country-selector-dropdown") {
          w.tag = w.tag || "dd";
        }
        for (var i = 0; i < LAYOUT_RULES.length; i++) {
          if (LAYOUT_RULES[i].re.test(key)) {
            w.order = String(LAYOUT_RULES[i].order);
            w.pair = LAYOUT_RULES[i].pair;
            break;
          }
        }
      });

      // Shopify Forms custom-select widgets (domeniu / volum) are anonymous
      // while collapsed — a bare <button aria-expanded> with no id/name/
      // aria-controls — so the loop above can't place them and they fall to
      // ORDER_DEFAULT. Opening one adds aria-controls, and the observer then
      // reclassifies the row (order 115 -> 50, full-width -> paired): the grid
      // reflows and the just-opened popover, anchored to the button's old
      // position, detaches upward. Their label/value nodes keep a stable
      // "<handle>-selector-*" id even collapsed — classify from that up front
      // so the layout is identical before and after opening. (The phone
      // country picker uses "country-selector-*" ids and is excluded.)
      if (w.order === ORDER_DEFAULT) {
        var selHook = el.querySelector(
          "[id$='-selector-value'],[id$='-selector-label'],[id$='-selector-dropdown']"
        );
        if (selHook && selHook.id.indexOf("country-selector") === -1) {
          w.key += " " + selHook.id;
          w.tag = w.tag || "dd";
          for (var di = 0; di < LAYOUT_RULES.length; di++) {
            if (LAYOUT_RULES[di].re.test(selHook.id)) {
              w.order = String(LAYOUT_RULES[di].order);
              w.pair = LAYOUT_RULES[di].pair;
              break;
            }
          }
        }
      }

      if (w.kind === "submit") {
        w.order = ORDER_SUBMIT;
      } else if (isField) {
        w.kind = w.kind || "rest";
        seenField = true;
      } else {
        // text-only block: the form's own title/description render above the
        // fields and would duplicate the card head — hide those; keep any
        // headings between fields (e.g. "Persoana de contact").
        w.kind = "text";
        w.hide = !seenField;
      }
      wrappers.push(w);
    });
    return { form: form, container: container, wrappers: wrappers };
  }

  function setDisp(el, show) {
    var v = show ? "" : "none";
    if (el.style.display !== v) el.style.display = v;
  }

  // ---- CUI controls: search button, status line, manual link ----------------

  function setStatus(root, msg, kind) {
    var el = root.getElementById("kregf-status");
    if (!el) return;
    if (el.textContent !== (msg || "")) el.textContent = msg || "";
    el.className = kind === "error" ? "is-error" : kind === "ok" ? "is-ok" : "";
  }

  function ensureCuiControls(root, s) {
    var cui = root.getElementById(CUI_ID);
    if (!cui) return;

    // "Verifică CUI" sits next to the input — flex the input's row.
    var row = cui.parentElement;
    if (row) {
      row.id = "kregf-cui-row"; // flex layout comes from SKIN_CSS
      if (!root.getElementById("kregf-search")) {
        var btn = document.createElement("button");
        btn.type = "button";
        btn.id = "kregf-search";
        btn.textContent = cfg("cui-button-label", "Verifică CUI");
        btn.addEventListener("click", function () {
          verify(root);
        });
        row.appendChild(btn);
      }
    }

    // status + manual link, after the CUI field's wrapper content
    var wrapEl = null;
    for (var i = 0; i < s.wrappers.length; i++) {
      if (s.wrappers[i].kind === "cui") wrapEl = s.wrappers[i].el;
    }
    if (wrapEl && !root.getElementById("kregf-status")) {
      var status = document.createElement("div");
      status.id = "kregf-status";
      wrapEl.appendChild(status);
    }
    if (wrapEl && !root.getElementById("kregf-manual")) {
      var manual = document.createElement("button");
      manual.type = "button";
      manual.id = "kregf-manual";
      manual.textContent = cfg(
        "manual-label",
        "Nu ai CUI la îndemână? Completează manual →"
      );
      manual.addEventListener("click", function () {
        anafLocked = false;
        reveal(root);
        var denumire = root.getElementById(NAME_ID);
        if (denumire) denumire.focus();
      });
      wrapEl.appendChild(manual);
    }

    // once a CUI validated against ANAF, the fields are locked to its data —
    // the manual-entry link no longer applies
    var manualBtn = root.getElementById("kregf-manual");
    if (manualBtn) setDisp(manualBtn, !anafLocked);

    if (!cui.dataset.kregfBound) {
      cui.dataset.kregfBound = "1";
      // Enter in the CUI field triggers the search instead of submitting.
      cui.addEventListener("keydown", function (e) {
        if (e.key === "Enter") {
          e.preventDefault();
          verify(root);
        }
      });
    }
  }

  // ---- ANAF lookup / reveal (flow mirrors b2b-register.js) ------------------

  function fill(root, d) {
    if (d.denumire) setReactValue(root.getElementById(NAME_ID), d.denumire);
    if (d.nrRegCom) setReactValue(root.getElementById(REG_ID), d.nrRegCom);
    setRadio(root, TVA_NAME, d.platitorTVA ? "Da" : "Nu");
  }

  // Text inputs use readOnly (a disabled input would be dropped from
  // submission); the TVA radio group is locked via pointer-events so the
  // checked value still submits.
  function applyAnafLock(root) {
    [NAME_ID, REG_ID].forEach(function (id) {
      var el = root.getElementById(id);
      if (!el) return;
      if (el.readOnly !== anafLocked) el.readOnly = anafLocked;
    });
    var radios = root.querySelectorAll(
      'input[type="radio"][name="' + TVA_NAME + '"]'
    );
    Array.prototype.forEach.call(radios, function (r) {
      var chip = r.closest("label") || r;
      var pe = anafLocked ? "none" : "";
      if (chip.style.pointerEvents !== pe) chip.style.pointerEvents = pe;
      var op = anafLocked ? "0.6" : "";
      if (chip.style.opacity !== op) chip.style.opacity = op;
    });
  }

  function reveal(root) {
    if (!revealed) revealed = true;
    applyAnafLock(root);
    safeApply(root);
  }

  function verify(root) {
    var cui = root.getElementById(CUI_ID);
    if (!cui) return;
    var digits = String(cui.value || "").replace(/[^0-9]/g, "");
    if (digits.length < MIN_CUI_LEN) {
      setStatus(root, "Introdu un CUI valid (minim 6 cifre).", "error");
      cui.focus();
      return;
    }
    var btn = root.getElementById("kregf-search");
    if (btn) btn.disabled = true;
    setStatus(root, "Se preiau datele de la ANAF…", null);
    fetch(PROXY_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ cui: digits }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error(r.status === 404 ? "negăsit" : "eroare");
        return r.json();
      })
      .then(function (d) {
        anafLocked = true; // valid CUI → lock the ANAF-sourced fields
        reveal(root);
        fill(root, d);
        applyAnafLock(root);
        var note = "Date completate automat de la ANAF.";
        if (d.stare && INACTIVE_RE.test(d.stare)) {
          note += " Atenție: firmă posibil inactivă.";
        }
        setStatus(root, note, "ok");
      })
      .catch(function (err) {
        // lookup failed → reveal the (empty, editable) fields for manual entry
        anafLocked = false;
        reveal(root);
        setStatus(
          root,
          err.message === "negăsit"
            ? "CUI negăsit la ANAF — verifică numărul sau completează manual."
            : "Nu s-au putut prelua datele. Completează manual datele firmei.",
          "error"
        );
      })
      .then(function () {
        if (btn) btn.disabled = false;
      });
  }

  // ---- labels & placeholders --------------------------------------------------
  // The embed renders Material-style floating labels inside the field box;
  // the kreg look wants the label above the field. Hoist each label's text
  // onto the wrapper (rendered by the [data-kregf-label]::before rule) and
  // keep the original label for screen readers only.

  var PLACEHOLDERS = [
    [/cui/i, "RO12345678"],
    [/company|denumire|firma/i, "DENTAPRO SRL"],
    [/comerului|reg_com/i, "J40/1234/2020"],
    [/first_name/i, "Andrei"],
    [/last_name/i, "Popescu"],
    [/phone/i, "07xx xxx xxx"],
    [/email/i, "nume@companie.ro"],
  ];

  // The form's own labels are managed in the Forms app (partly English);
  // known fields get the native card's Romanian wording instead.
  var LABEL_OVERRIDES = [
    [/cui/i, "CUI firmă"],
    [/company|denumire/i, "Denumire firmă"],
    [/comerului|reg_com/i, "Nr. Registrul Comerțului"],
    [/email/i, "Email de business"],
  ];

  function labelText(el) {
    return el.textContent.replace(/\s+/g, " ").replace(/\s*\*\s*$/, "").trim();
  }

  function manageLabels(s) {
    s.wrappers.forEach(function (w) {
      if (w.kind === "pw" || w.kind === "submit" || w.kind === "text") return;
      if (w.tag === "check") return; // the consent label stays inline
      // group labels (TVA radios, checkbox groups) come from the legend;
      // dropdown widgets label with a <p>; text fields with <label for>
      var lab =
        w.el.querySelector("legend") ||
        w.el.querySelector("label[for]") ||
        (w.tag === "dd" ? w.el.querySelector("p") : null);
      if (!lab) return;
      var text = labelText(lab);
      for (var i = 0; i < LABEL_OVERRIDES.length; i++) {
        if (LABEL_OVERRIDES[i][0].test(w.key)) {
          text = LABEL_OVERRIDES[i][1];
          break;
        }
      }
      if (!text) return;
      if (w.el.getAttribute("data-kregf-label") !== text) {
        w.el.setAttribute("data-kregf-label", text);
      }
      if (!lab.classList.contains("kregf-sr")) lab.classList.add("kregf-sr");
    });

    Array.prototype.forEach.call(
      s.form.querySelectorAll("input,textarea"),
      function (c) {
        if (
          c.type === "hidden" || c.type === "radio" ||
          c.type === "checkbox" || c.type === "submit" || c.type === "button"
        ) return;
        var key = fieldKey(c);
        for (var i = 0; i < PLACEHOLDERS.length; i++) {
          if (PLACEHOLDERS[i][0].test(key)) {
            if (c.placeholder !== PLACEHOLDERS[i][1]) {
              c.placeholder = PLACEHOLDERS[i][1];
            }
            break;
          }
        }
      }
    );
  }

  // ---- note / foot templates from the light DOM ------------------------------

  function ensureTemplateClone(root, s, tplAttr, id, order) {
    var el = root.getElementById(id);
    if (!el) {
      var wrap = getWrap();
      var tpl = wrap && wrap.querySelector("template[" + tplAttr + "]");
      if (!tpl) return null;
      el = document.createElement("div");
      el.id = id;
      el.innerHTML = tpl.innerHTML;
      s.container.appendChild(el);
    }
    el.style.order = order;
    return el;
  }

  // The submit button label follows the section setting; swap only the text
  // node so React's reconciliation of the button isn't broken.
  function manageSubmit(s) {
    var btn = s.form.querySelector('button[type="submit"]');
    if (!btn) return;
    var label = cfg("submit-label", "");
    if (!label) return;
    var textNode = findTextNode(btn);
    if (textNode && textNode.nodeValue !== label) textNode.nodeValue = label;
  }

  // Before the reveal only the CUI field is visible; Enter/submit then acts
  // as "Verifică CUI" instead of submitting a mostly-empty form.
  function bindSubmitGuard(s, root) {
    if (s.form.dataset.kregfGuard) return;
    s.form.dataset.kregfGuard = "1";
    s.form.addEventListener(
      "submit",
      function (e) {
        if (!revealed) {
          e.preventDefault();
          e.stopPropagation();
          verify(root);
        }
      },
      true
    );
  }

  // ---- apply -----------------------------------------------------------------

  // The app caps its inline container at 450px (--inline-container-max-width);
  // the kreg card wants 600. The variable is defined by the app's own
  // stylesheet somewhere in the shadow tree, so re-declare it inline on the
  // form's whole ancestor chain + host — inline styles always win.
  function widenContainer(root, s) {
    var el = s.form;
    while (el && el !== root) {
      if (el.style && el.style.getPropertyValue("--inline-container-max-width") !== "600px") {
        el.style.setProperty("--inline-container-max-width", "600px");
      }
      el = el.parentElement;
    }
    if (root.host && root.host.style.getPropertyValue("--inline-container-max-width") !== "600px") {
      root.host.style.setProperty("--inline-container-max-width", "600px");
    }
  }

  function apply(root) {
    var s = scan(root);
    if (!s) return; // form gone — the embed shows its own success message
    ensureStyle(root);
    widenContainer(root, s);
    bindSubmitGuard(s, root);
    ensureCuiControls(root, s);
    manageLabels(s);
    manageSubmit(s);
    applyAnafLock(root);

    // 2-column grid, but every field spans the full row except the
    // domeniu/volum pair (LAYOUT_RULES pair:true) — those auto-place side
    // by side. `order` arranges the rows to mirror the native card's
    // sequence. The mobile collapse to one column lives in SKIN_CSS
    // (#kregf-grid media query), which the id hooks into.
    if (s.container.id !== "kregf-grid") s.container.id = "kregf-grid";
    if (s.container.style.display !== "grid") {
      s.container.style.display = "grid";
      s.container.style.gridTemplateColumns = "repeat(2,minmax(0,1fr))";
      s.container.style.maxWidth = "100%";
      s.container.style.gap = "16px";
    }

    s.wrappers.forEach(function (w) {
      var show;
      if (w.kind === "pw" || w.hide) show = false;
      else if (w.kind === "cui") show = true;
      else show = revealed;
      setDisp(w.el, show);
      if (w.el.style.order !== w.order) w.el.style.order = w.order;
      var span = w.pair ? "auto" : "1 / -1";
      if (w.el.style.gridColumn !== span) w.el.style.gridColumn = span;
      if (w.el.getAttribute("data-kregf") === null) {
        w.el.setAttribute("data-kregf", "");
      }
      if (w.tag && w.el.getAttribute("data-kregf-kind") !== w.tag) {
        w.el.setAttribute("data-kregf-kind", w.tag);
      }
    });

    var note = ensureTemplateClone(root, s, "data-kreg-note", "kregf-note", ORDER_NOTE);
    if (note) {
      note.style.gridColumn = "1 / -1";
      setDisp(note, revealed);
    }
    var foot = ensureTemplateClone(root, s, "data-kreg-foot", "kregf-foot", ORDER_FOOT);
    if (foot) {
      foot.style.gridColumn = "1 / -1";
      setDisp(foot, revealed);
    }
  }

  function safeApply(root) {
    if (applying) return;
    applying = true;
    try {
      apply(root);
    } finally {
      applying = false;
    }
  }

  // The embed renders async and React re-renders nodes; poll until the form
  // exists, then keep an observer to re-apply after re-renders. All apply
  // work is idempotent so the observer never loops.
  function init() {
    if (!getWrap()) return;
    var tries = 0;
    var iv = setInterval(function () {
      var root = getRoot();
      if (root && root.querySelector("form")) {
        safeApply(root);
        var mo = new MutationObserver(function () {
          safeApply(root);
        });
        mo.observe(root, { childList: true, subtree: true });
        clearInterval(iv);
      } else if (++tries > 50) {
        clearInterval(iv); // give up after ~15s
      }
    }, 300);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
